import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useSelector } from "react-redux";
import useCustomSnackbar from "../../../../hooks/useCustomSnackbar";
import { copySharableURL, view } from "../../../../utils/API";
import {
  EMBED_VARIANTS,
  getExcludeFields,
  renderCell,
  ROLE_EDITOR,
  standardizeColor,
  timeAgo,
} from "../../../../utils/helper";
import { getLayerSymbology } from "../../../../utils/symbologies";
import ClusterDetails from "../../../CustomPopupWrapper/components/ClusterDetails/ClusterDetails";
import getAllowedFields from "../../../CustomPopupWrapper/helpers/getAllowedFields";
import getPopupActions from "../../../CustomPopupWrapper/helpers/getPopupActions";
import { StyledFilterButton } from "../../../Dashboard/Insights/Insights-styled";

import { Tooltip } from "../../../UI";
import FeatureDetailsTabs from "../FeatureDetailsTab";
import { TABS } from "../FeatureDetailsTab/FeatureDetailsTab";
import {
  StyledField,
  StyledInformation,
  StyledPopupWrapper,
} from "../MobilePopup-styled";
import Documents from "./Documents";
import FeatureHeading from "./FeatureHeading/FeatureHeading";
import FieldsSection from "./FieldsSection";
import PopupField from "./FieldsSection/PopupField";
import ImageSwiper from "./ImageSwiper/ImageSwiper";

const currSourceFieldName = "currsourcename";
const currInfoRelyFieldName = "currinforely";
const currAsOfDateFieldName = "currasofdate";

const updatesInfoFields = [
  currAsOfDateFieldName,
  currSourceFieldName,
  currInfoRelyFieldName,
];

export const getLayerConfigFields = ({
  layer,
  layerConfigFields,
  symbologyFields,
  feature,
  t,
  config,
}) => {
  const symbologyFieldsSet = new Set(
    symbologyFields?.map((f) => f.field) || []
  );

  const fieldMap = new Map();
  layer.fields.forEach((field) => {
    fieldMap.set(field.name, field);
  });

  const results = [];
  const fieldSet = new Set(layerConfigFields);
  fieldSet.forEach((fieldName) => {
    if (symbologyFieldsSet.has(fieldName)) return;

    const field = fieldMap.get(fieldName);
    if (field) {
      const attrValue = feature.attributes[field.name];
      if (attrValue !== null && attrValue !== undefined) {
        const value = renderCell(
          field,
          feature.attributes[field.name],
          t,
          config
        );

        results.push({
          title: t("layer.fieldAlias." + field.name + ".title", field.alias),
          value,
          fieldName: field.name,
          attrValue,
        });
      }
    }
  });

  // const fields = [];
  // layerConfigFields.forEach((fieldName) => {
  //   if (symbologyFields.some((f) => f.field === fieldName)) return;

  //   const field = layer.fields.find((field) => field.name === fieldName);
  //   if (field) {
  //     fields.push(field);
  //   }
  // });

  // const results = [];
  // fields.forEach((field) => {
  //   const attrValue = feature.attributes[field.name];

  //   if (attrValue !== null && attrValue !== undefined) {
  //     const value = renderCell(
  //       field,
  //       feature.attributes[field.name],
  //       t,
  //       config
  //     );
  //     results.push({
  //       title: t("layer.fieldAlias." + field.name + ".title", field.alias),
  //       value,
  //       fieldName: field.name,
  //     });
  //   }
  // });

  return results;
};

const PopupBody = ({
  config,
  selectedFeature,
  expand,
  setFullScreenActive,
  t,
  i18n,
  isPopup = false,
}) => {
  const layer = selectedFeature.sourceLayer
    ? selectedFeature.sourceLayer
    : selectedFeature.layer;

  const fields = layer.fields;
  const layerConfig = layer.layerConfig;
  const layerSettings = layerConfig.customPopupOps;
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const clusterFeature = useSelector((state) => state.clusterFeature);
  const activeModule = useSelector((state) => state.activeModule);
  const [openSnackbar] = useCustomSnackbar();
  const [isPending, startTransition] = useTransition();
  const headerRef = useRef();

  useEffect(() => {
    setImages([]);
    if (!layer.capabilities.operations.supportsQueryAttachments) return;

    layer
      .queryAttachments({
        objectIds: [selectedFeature.attributes[layer.objectIdField]],
      })
      .then((response) => {
        const attachments = Object.values(response).flat(1);

        startTransition(() => {
          setImages(
            attachments?.filter((att) => att.contentType.startsWith("image")) ||
              []
          );
          setAttachments(
            attachments?.filter(
              (att) => !att.contentType.startsWith("image")
            ) || []
          );
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedFeature, fields, layer]);

  const allowedFields = useMemo(() => {
    return getAllowedFields(layer, selectedFeature);
  }, [layer, selectedFeature]);

  const getFields = (filterFields) => {
    if (!Array.isArray(filterFields)) return null;

    return allowedFields.filter(
      (field) => filterFields.includes(field.name) && !!field
    );
  };

  const handleCopyLink = () => {
    const clonedFeature = selectedFeature.clone();
    if (
      view?.popup?.location &&
      clonedFeature.geometry &&
      clonedFeature.geometry?.type !== "point"
    ) {
      clonedFeature.geometry = view.popup.location;
    }

    copySharableURL({
      feature: selectedFeature,
      config,
      activeModule,
      openSnackbar,
      t,
    });
  };

  const handleEdit = () => {
    const editor = view.ui.find("Editor");

    setTimeout(() => {
      editor.expanded = true;
    }, 0);
  };

  const symbologyFields = useMemo(() => {
    const layer = selectedFeature.sourceLayer ?? selectedFeature.layer;
    const result = [];
    if (layer) {
      const symboloy = getLayerSymbology(layer, config);
      const { colorMap } = symboloy || {};

      if (colorMap) {
        const colorMapFields = colorMap.field
          ? [colorMap.field]
          : colorMap.fields;
        const layerFields = layer.fields.filter((field) =>
          colorMapFields.includes(field.name)
        );

        const colors = layerFields.map((field) => {
          const color =
            colorMap.default[selectedFeature.attributes[field.name]] ??
            symboloy.defaultColor;
          const standartColor = standardizeColor(color);
          if (color)
            return {
              value: renderCell(
                field,
                selectedFeature.attributes[field.name],
                t,
                config
              ),
              bg: standartColor.includes("#ffffff") ? "#000" : standartColor,
              color: "#fff",
              field: field.name,
              title: t(
                "layer.fieldAlias." + field.name + ".title",
                field.alias
              ),
            };
        });
        result.push(...colors);
      }
    }
    return result;
  }, [selectedFeature, config, t]);

  const overviewFields = useMemo(() => {
    return [
      ...getFields(layerSettings.overviewTable1Fields),
      ...getFields(layerSettings.overviewTable2Fields),
    ].filter((field) => !symbologyFields.some((f) => f.field === field.name));
  }, [layerSettings, symbologyFields]);

  const restFields = useMemo(() => {
    return allowedFields.filter(
      (field) =>
        !(
          overviewFields.some((f) => f.name === field.name) ||
          symbologyFields.some((f) => f.field === field.name)
        )
    );
  }, [layer, selectedFeature, overviewFields, symbologyFields]);

  const getSituationalFields = useCallback(() => {
    if (!Array.isArray(layer?.layerConfig.situationalFields)) return [];
    const excludeFields = getExcludeFields(layer);
    const excludeFieldsSet = new Set(excludeFields || []);
    const excludeFromPopup =
      layer.layerConfig?.customPopupOps?.excludeFromPopup || [];
    excludeFieldsSet.add(...excludeFromPopup);
    excludeFromPopup.forEach((item) => excludeFieldsSet.add(item));

    const res = getLayerConfigFields({
      layer,
      layerConfigFields: layer.layerConfig.situationalFields.filter(
        (item) => !excludeFieldsSet.has(item)
      ),
      config,
      t,
      feature: selectedFeature,
      symbologyFields,
    });

    return res.filter((item) => !updatesInfoFields.includes(item.fieldName));
  }, [layer, symbologyFields, selectedFeature, config, t]);

  const situationalFields = getSituationalFields();

  const updateFields = useMemo(() => {
    const fields = getLayerConfigFields({
      layer,
      layerConfigFields: updatesInfoFields,
      config,
      t,
      feature: selectedFeature,
      symbologyFields,
    });

    const result = {};

    fields.forEach((info) => {
      if (
        info.fieldName === "currinforely" &&
        (info.attrValue === null || info.attrValue === 0)
      ) {
        return;
      }

      result[info.fieldName] = info;
    });

    return result;
  }, [layer, symbologyFields, selectedFeature, config, t]);

  const baseLineFields = useMemo(() => {
    if (!Array.isArray(layer?.layerConfig.baselineFields)) return [];
    const excludeFields = getExcludeFields(layer);
    const excludeFieldsSet = new Set(excludeFields || []);
    const excludeFromPopup =
      layer.layerConfig?.customPopupOps?.excludeFromPopup || [];
    excludeFieldsSet.add(...excludeFromPopup);

    const res = getLayerConfigFields({
      layer,
      layerConfigFields: layer.layerConfig.baselineFields.filter(
        (item) => !excludeFieldsSet.has(item)
      ),
      config,
      t,
      feature: selectedFeature,
      symbologyFields,
    });

    return res;
  }, [layer, symbologyFields, selectedFeature, config, t]);

  const tabs = useMemo(() => {
    const result = [];
    if (selectedFeature.attributes.lcapageid) {
      result.push(TABS.LCA);
    }
    return result;
  }, [selectedFeature, attachments]);

  const format = (value) => {
    if (!value) return;
    return timeAgo(new Date(value), i18n.language, false);
  };

  const statusColor = useMemo(() => {
    try {
      const symbology = getLayerSymbology(layer, config) || {};

      if (symbologyFields.length > 0) {
        return standardizeColor(symbologyFields[0].bg);
      } else if (symbology?.defaultColor) {
        return standardizeColor(symbology.defaultColor);
      }
    } catch (err) {
      console.log(err);
    }

    return "#a6a6a6";
  }, [symbologyFields, config, layer]);

  const showUpdateText = useMemo(() => {
    return (
      updateFields[currAsOfDateFieldName]?.value ||
      updateFields[currSourceFieldName]?.value ||
      updateFields[currInfoRelyFieldName]?.value
    );
  }, []);

  const actionButtons = useMemo(
    () => getPopupActions({ t, layer, config }),
    [t, layer, config]
  );
  const isAppEmbed = useMemo(
    () => config.embed === EMBED_VARIANTS.APP,
    [config]
  );

  const shareButton = useMemo(() => {
    let isUpdate = false;
    if ("reporttype" in selectedFeature?.attributes) {
      isUpdate = selectedFeature?.attributes?.reporttype === 2;
    }

    if (isUpdate) return null;

    const shareUpdateButtonIndex = actionButtons.findIndex(
      (btn) => btn.id === "report-update"
    );

    if (shareUpdateButtonIndex > -1) {
      return {
        index: shareUpdateButtonIndex,
        button: actionButtons[shareUpdateButtonIndex],
      };
    }

    return null;
  }, [actionButtons, selectedFeature]);

  const handleReportUpdate = useCallback(() => {
    if (!shareButton) return;
    view.popup.triggerAction(shareButton.index);
  }, [view, shareButton]);

  const renderUpdateText = () => {
    const [informationUpdated, on, from] = t("screen.popup.update", "")
      .split("...")
      .map((item) => item.trim());

    let title = informationUpdated;
    const date = updateFields[currAsOfDateFieldName]?.value;
    const source = updateFields[currSourceFieldName]?.value;
    const rely = updateFields[currInfoRelyFieldName]?.value;
    if (date) {
      title += ` ${on} <span>${date}</span>`;
    }

    if (source) {
      title += ` ${from} <span>${source}</span>`;
    }

    if (rely) {
      if (!source) {
        title += ` ${from}`;
      }
      title += `<span> (${rely})</span>`;
    }

    return title;
  };

  const isArabic = useMemo(() => {
    return i18n.language === "ar";
  }, [i18n.language]);

  const showSituationalBox = useMemo(() => {
    return (
      (!isAppEmbed && !!shareButton) ||
      situationalFields.length > 0 ||
      symbologyFields.length > 0 ||
      showUpdateText
    );
  }, [symbologyFields, situationalFields, isAppEmbed, shareButton]);

  return (
    <div>
      {!!clusterFeature && (
        <div style={{ padding: "10px 8px 0px" }}>
          <ClusterDetails
            fields={allowedFields}
            feature={selectedFeature}
            t={t}
            config={config}
            layer={layer}
          />
        </div>
      )}
      <StyledPopupWrapper isPopup={isPopup} isCluster={!!clusterFeature}>
        <div
          ref={headerRef}
          style={{
            position: "sticky",
            top: "0px",
            background: "#fff",
            padding: "8px 8px 0px 8px",
            zIndex: 1,
          }}
        >
          <FeatureHeading
            feature={selectedFeature}
            t={t}
            handleShare={handleCopyLink}
            actionButtons={actionButtons}
            expanded={expand}
            isPopup={isPopup}
            i18n={i18n}
          />
        </div>
        {showSituationalBox && (
          <div
            id="popup-situational-fields"
            style={{
              border: `1px solid ${statusColor}4d`,
              borderRadius: 8,
              margin: 4,
              padding: "2px 0px",
            }}
          >
            <div
              style={{
                padding: "0px 8px 0px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {symbologyFields.map((field) => (
                <StyledField
                  key={field.title}
                  isPopup={isPopup}
                  dir={isArabic ? "rtl" : ""}
                >
                  <p>
                    <span>
                      {field.title + ": "}{" "}
                      <span
                        style={{ color: field.bg, fontWeight: 600 }}
                        dangerouslySetInnerHTML={{ __html: field.value }}
                      />
                    </span>
                  </p>
                </StyledField>
              ))}
              {situationalFields.length > 0 &&
                situationalFields
                  .slice(
                    0,
                    expand || !!clusterFeature ? situationalFields.length : 1
                  )
                  .map((field) => (
                    <PopupField
                      key={field.title}
                      title={field.title}
                      value={field.value}
                      resetLimit={!expand}
                      onClick={() => {
                        setFullScreenActive(true);
                      }}
                      isPopup={isPopup}
                      i18n={i18n}
                      feature={selectedFeature}
                      field={field}
                      t={t}
                      isExpanded={expand}
                      fromPopup
                    />
                  ))}
              {showUpdateText && (
                <StyledInformation
                  dir={isArabic ? "rtl" : ""}
                  isPopup={isPopup}
                  dangerouslySetInnerHTML={{ __html: renderUpdateText() }}
                />
              )}

              {!isAppEmbed && !!shareButton && (
                <StyledFilterButton
                  onClick={handleReportUpdate}
                  disabled={config.role === ROLE_EDITOR}
                  style={{
                    margin: "auto",
                    marginTop: 4,
                    marginBottom: 8,
                    borderRadius: 100,
                    background:
                      config.role === ROLE_EDITOR
                        ? "var(--elementBackground)"
                        : statusColor,
                    borderColor:
                      config.role === ROLE_EDITOR
                        ? "var(--elementBackground)"
                        : statusColor,
                    color:
                      config.role === ROLE_EDITOR ? "var(--fontColor)" : "#fff",
                    padding: "4px 10px",
                    fontSize: 14,
                    position: "relative",
                    cursor: config.role === ROLE_EDITOR ? "default" : undefined,
                  }}
                >
                  {config.role === ROLE_EDITOR && (
                    <Tooltip
                      placement="top"
                      content="This functionality is only available for public users"
                      wrapperStyles={{
                        cursor: "default",
                      }}
                      absolute
                      showOnHover
                    />
                  )}
                  {shareButton.button.title}
                </StyledFilterButton>
              )}
            </div>
          </div>
        )}
        {expand && (
          <>
            <ImageSwiper images={images} height={"200px"} />
            <Documents attachments={attachments} t={t} />
            <div
              style={{
                padding: "8px 8px 4px",
              }}
            >
              {baseLineFields.length > 0 && (
                <FieldsSection
                  title={
                    showSituationalBox ? t("screen.popup.actions.seeMore") : ""
                  }
                  fields={baseLineFields}
                  show={isPopup ? !showSituationalBox : tabs.length === 0}
                  setFullScreenActive={setFullScreenActive}
                  resetFields={expand}
                  isPopup={isPopup}
                  hasLca={!!selectedFeature.attributes.lcapageid}
                  i18n={i18n}
                  t={t}
                  feature={selectedFeature}
                />
              )}
            </div>
            <FeatureDetailsTabs
              config={config}
              feature={selectedFeature}
              layer={layer}
              attachments={attachments}
              tabs={tabs}
              t={t}
              headerRef={headerRef}
            />
          </>
        )}
      </StyledPopupWrapper>
    </div>
  );
};

export default PopupBody;
